import { useEffect, useRef } from 'react'
import Nchan from 'nchan'
import { useDispatch } from 'react-redux'
import { useTranslation, Trans } from 'react-i18next'
import { useToast, Box, List, Text } from '@machobear/design-system'
import useSession from '@/hooks/use-current-session'
import config from '@/config'
import queryClient from '@/components/query-client'

const { NCHAN_SERVER } = config

const STORAGE_KEY = '@SSE:lastEventId'

const NCHAN_OPTIONS = {
  subscriber: 'eventsource',
  shared: false,
  reconnect: false
}

const getOptions = () => {
  let lastMessageId
  try {
    lastMessageId = localStorage.getItem(STORAGE_KEY)
  } catch (err) {
    console.log('Storage::error', err)
  }
  return {
    ...NCHAN_OPTIONS,
    msgId: lastMessageId
  }
}

export type MessageLike = {
  type: string
}
type Dict = { [key: string]: string }
export type Handler<T extends MessageLike = MessageLike> = (x: T) => void

export const SSE = () => {
  const sub = useRef<Nchan | null>(null)
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const session = useSession()
  const showToast = useToast()
  const uuid = session?.uuid
  const packageBlocks: Dict = {
    analytics_report: t('l.sse.analytics_report.title', 'Analytics Report'),
    automated_rent_collections: t(
      'l.sse.automated_rent_collections.title',
      'Automated Rent Collections'
    ),
    credit_checks: t('l.sse.tenant_screening_report.title', 'Tenant Screening Report'),
    custom_addenda: t('l.sse.custom_addenda.title', 'Custom Addenda'),
    custom_contracts: t('l.sse.custom_contracts.title', 'Custom Contracts'),
    managed_users: t('l.sse.managed_users.title', 'Managed Users'),
    syndication: t('l.sse.syndication.title', 'Syndication'),
    we_chat: t('l.sse.we_chat.title', 'We Chat')
  }

  const purchasedBlocks: Dict = {
    credit_checks_additional_packages: t(
      'l.sse.tenant_screening_report.title',
      'Tenant Screening Report'
    ),
    sponsored_listing_days: t('l.sse.sponsored_listings.title', 'Sponsored Listings'),
    syndication_additional_packages: t('l.sse.syndication.title', 'Syndication')
  }

  useEffect(() => {
    if (!uuid) return
    if (!sub.current) {
      sub.current = new Nchan(`${NCHAN_SERVER}/${uuid}`, getOptions())
    }
    sub.current.on('transportSetup', (opt: any) => {
      opt.eventsource.withCredentials = true
    })
    sub.current.on('message', (payload, meta) => {
      const message = JSON.parse(payload)

      const handlers = new Map([
        [
          // this sse is sent for purchasing new credits only, consuming credits will need to be update by invalidating the blocks query
          'blocks_updated',
          (message: { type: string; data: any }) => {
            const packages = message?.data ?? {}
            const planData = [] as any
            const purchasedData = [] as any
            Object.entries(packages).forEach(([type, data]) => {
              if (Object.keys(packageBlocks).includes(type)) {
                planData.push({ type, data })
              }
              if (Object.keys(purchasedBlocks).includes(type)) {
                purchasedData.push({ type, data })
              }
            })

            if (planData.length > 0) {
              showToast({
                key: `plan-credits-${planData.length}`,
                message: t('l.usage_credits.plan_credits_update.title', 'Plan credits updated!'),
                tone: 'positive',
                description: (
                  <Box paddingLeft="xxsmall">
                    <List>
                      {planData.map((item: { type: string; data: any }) => {
                        const type = packageBlocks[item.type]
                        const quantity = item?.data?.increased_quantity
                        return (
                          <Text key={item?.type}>
                            {item?.type === 'sponsored_listing_days' ? (
                              <Trans
                                i18nKey={'l.sse.increased_package_quantity_days.text'}
                                t={t}
                                type={type}
                              >
                                {{ type }}: {{ quantity }} day(s) added!
                              </Trans>
                            ) : (
                              <Trans
                                i18nKey={'l.sse.increased_package_quantity.text'}
                                t={t}
                                type={type}
                              >
                                {{ type }}: {{ quantity }} credit(s) added!
                              </Trans>
                            )}
                          </Text>
                        )
                      })}
                    </List>
                  </Box>
                )
              })
            }
            if (purchasedData.length > 0) {
              showToast({
                key: `purchased-credits-${purchasedData.length}`,
                message: t(
                  'l.usage_credits.purchased_credits_update.title',
                  'Purchased credits updated!'
                ),
                tone: 'positive',
                description: (
                  <Box paddingLeft="xxsmall">
                    <List>
                      {purchasedData.map((item: { type: string; data: any }) => {
                        const type = purchasedBlocks[item.type]
                        const quantity = item?.data?.increased_quantity
                        return (
                          <Text key={item?.type}>
                            {item?.type === 'sponsored_listing_days' ? (
                              <Trans
                                i18nKey={'l.sse.increased_purchased_quantity_days.text'}
                                t={t}
                                type={type}
                              >
                                {{ type }}: {{ quantity }} day(s) added!
                              </Trans>
                            ) : (
                              <Trans
                                i18nKey={'l.sse.increased_purchased_quantity.text'}
                                t={t}
                                type={type}
                              >
                                {{ type }}: {{ quantity }} credit(s) added!
                              </Trans>
                            )}
                          </Text>
                        )
                      })}
                    </List>
                  </Box>
                )
              })
            }
            if (Object.keys(message?.data).includes('sponsored_listing_days')) {
              queryClient.invalidateQueries({ queryKey: ['blocks', 'sponsored_listing_days'] })
            }

            queryClient.invalidateQueries({ queryKey: ['blocks'] })
          }
        ],
        [
          'queued_credit_report_executed',
          (message: { type: string; data: any }) => {
            showToast({
              message: t(
                'l.standalone_trustscore.list.toast.completed_form',
                'Trust Score completed!'
              ),
              tone: 'positive'
            })
            queryClient.invalidateQueries({ queryKey: ['trustScoreList'] })
            queryClient.invalidateQueries({ queryKey: ['blocks'] })
          }
        ]
      ]) as Map<string, Handler>
      handlers.get(message.type)?.(message)
      dispatch.sse.dispatchMessage(message)

      localStorage.setItem(STORAGE_KEY, meta.id.split(',')[0])
    })
    !sub.current.running && sub.current.start()

    return () => {
      sub.current?.running && sub.current.stop()
    }
  }, [uuid])

  return null
}
